import { logBitacora, ACCIONES, MODULOS } from "@/data/adminBitacoraStore";
import { api } from "@/services/api";

/**
 * Store administrativo de Eventos.
 *
 * Ola 1 del backend: la fuente de verdad es la API FastAPI + SQLAlchemy.
 * - `hidratarEventos()` (async) sincroniza el cache local con `/api/eventos`.
 * - Los reads (`listarEventos`, `getEventoById`, ...) siguen siendo síncronos
 *   y leen del cache en `localStorage` para no forzar refactor de UI.
 * - Los writes (`guardarEvento`, `publicarEvento`, `cancelarEvento`,
 *   `cerrarEvento`, `duplicarEvento`) ahora son async y devuelven la entidad
 *   confirmada por el backend; también actualizan el cache tras persistir.
 *
 * Las funciones puras (checklist, adaptación al socio, cálculos de mesas,
 * formateo) no cambian.
 */

import { eventosMock } from "@/data/eventosMock";

const KEY_EVENTOS = "ct_admin_eventos_v1";

/* ---------- Enums ---------- */

export const ESTADO_EVENTO = {
  BORRADOR: "BORRADOR",
  PUBLICADO: "PUBLICADO",
  VENTA_ACTIVA: "VENTA_ACTIVA",
  VENTA_PAUSADA: "VENTA_PAUSADA",
  VENTA_FINALIZADA: "VENTA_FINALIZADA",
  EVENTO_REALIZADO: "EVENTO_REALIZADO",
  EVENTO_CERRADO: "EVENTO_CERRADO",
  CANCELADO: "CANCELADO",
};

export const ESTADO_EVENTO_LABEL = {
  BORRADOR: "Borrador",
  PUBLICADO: "Publicado",
  VENTA_ACTIVA: "Venta activa",
  VENTA_PAUSADA: "Venta pausada",
  VENTA_FINALIZADA: "Venta finalizada",
  EVENTO_REALIZADO: "Evento realizado",
  EVENTO_CERRADO: "Evento cerrado",
  CANCELADO: "Cancelado",
};

export const ESTADO_EVENTO_TONE = {
  BORRADOR: "neutral",
  PUBLICADO: "info",
  VENTA_ACTIVA: "success",
  VENTA_PAUSADA: "warning",
  VENTA_FINALIZADA: "muted",
  EVENTO_REALIZADO: "muted",
  EVENTO_CERRADO: "muted",
  CANCELADO: "danger",
};

export const FILTROS_EVENTO = [
  { id: "todos", label: "Todos" },
  { id: "proximos", label: "Próximos" },
  { id: "venta_activa", label: "Venta activa" },
  { id: "venta_pausada", label: "Venta pausada" },
  { id: "finalizados", label: "Finalizados" },
  { id: "cancelados", label: "Cancelados" },
];

export const TIPOS_EVENTO = [
  "Gala",
  "Cena",
  "Torneo",
  "Concierto",
  "Fiesta temática",
  "Deportivo",
  "Cultural",
  "Otro",
];

// NOTA: el catálogo oficial de salones seleccionables proviene EXCLUSIVAMENTE
// del backend (`GET /api/plantillas/salones-catalogo`), consumido vía
// `data/salonesCatalogoStore.js`. Aquí NO se mantiene ninguna lista oficial.
// Los nombres legacy sólo sobreviven internamente en `ELEMENTOS_FIJOS_POR_SALON`
// para renderizar eventos históricos; nunca se ofrecen como opción nueva.

export const TIPO_ACCESO = {
  SOLO_SOCIOS: "SOLO_SOCIOS",
  SOCIOS_INVITADOS: "SOCIOS_INVITADOS",
};

export const TIPO_ACCESO_LABEL = {
  SOLO_SOCIOS: "Solo socios",
  SOCIOS_INVITADOS: "Socios e invitados",
};

/* ---------- Mesas · Paso 3 ---------- */

export const TIPO_MESA = {
  REDONDA: "REDONDA",
  CUADRADA: "CUADRADA",
  RECTANGULAR: "RECTANGULAR",
};

export const TIPO_MESA_LABEL = {
  REDONDA: "Redonda",
  CUADRADA: "Cuadrada",
  RECTANGULAR: "Rectangular",
};

export const CAPACIDADES_MESA = [4, 6, 8, 10, 12, 20];

export const ESTADO_MESA = {
  DISPONIBLE: "DISPONIBLE",
  RESERVADA: "RESERVADA",
  PROTOCOLO: "PROTOCOLO",
  BLOQUEADA: "BLOQUEADA",
};

export const ESTADO_MESA_LABEL = {
  DISPONIBLE: "Disponible",
  RESERVADA: "Reservada",
  PROTOCOLO: "Protocolo",
  BLOQUEADA: "Bloqueada",
};

const ELEMENTOS_FIJOS_GENERICOS = [
  { id: "escenario", tipo: "escenario", label: "Escenario", x: 35, y: 4, w: 30, h: 10 },
  { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
  { id: "bar", tipo: "bar", label: "Bar", x: 4, y: 55, w: 8, h: 14 },
  { id: "banos", tipo: "banos", label: "Baños", x: 88, y: 55, w: 8, h: 14 },
];

const ELEMENTOS_FIJOS_POR_SALON = {
  // Salón de Los Candiles · resolver visual específico conforme al plano real:
  // escenario a la derecha, pista central, elevador de servicio arriba-derecha
  // y escaleras/acceso abajo-centro. NO usa los genéricos BAR/BAÑOS/ACCESO.
  // (Los elementos fijos se derivan del salón; nunca se guardan en la plantilla.)
  "Salón de Los Candiles": [
    { id: "escenario", tipo: "escenario", label: "Escenario", x: 87, y: 20, w: 11, h: 58 },
    { id: "pista", tipo: "pista", label: "Pista", x: 39, y: 30, w: 36, h: 34 },
    { id: "elevador", tipo: "elevador", label: "Elevador de servicio", x: 85, y: 4, w: 13, h: 13 },
    { id: "escaleras", tipo: "acceso", label: "Escaleras / Acceso", x: 42, y: 87, w: 18, h: 10 },
    { id: "columna-1", tipo: "columna", label: "Col.", x: 19, y: 58, w: 3, h: 7 },
    { id: "columna-2", tipo: "columna", label: "Col.", x: 78, y: 40, w: 3, h: 7 },
  ],
  "Salón Primera Sección de Los Candiles": ELEMENTOS_FIJOS_GENERICOS,
  "Salón Inglés (Disco)": [
    { id: "escenario", tipo: "escenario", label: "DJ / Escenario", x: 35, y: 4, w: 30, h: 10 },
    { id: "bar", tipo: "bar", label: "Bar", x: 4, y: 45, w: 8, h: 20 },
    { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
  ],
  "Comedor Privado": ELEMENTOS_FIJOS_GENERICOS,
  "Biblioteca": ELEMENTOS_FIJOS_GENERICOS,
  "Salón Miramar": ELEMENTOS_FIJOS_GENERICOS,
  "Salón Terraza": [
    { id: "escenario", tipo: "escenario", label: "Escenario", x: 35, y: 4, w: 30, h: 10 },
    { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
    { id: "bar", tipo: "bar", label: "Bar", x: 4, y: 55, w: 8, h: 14 },
  ],
  "Sala de Juntas del Consejo": [
    { id: "escenario", tipo: "mesa-central", label: "Mesa del Consejo", x: 25, y: 30, w: 50, h: 40 },
    { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
  ],
  // --- Alias internos SOLO para renderizar eventos históricos (no oficiales,
  // no se ofrecen para nuevas plantillas ni nuevos eventos). ---
  "Salón Imperial": ELEMENTOS_FIJOS_GENERICOS,
  "Salón del Puerto": ELEMENTOS_FIJOS_GENERICOS,
  "Salón Principal": ELEMENTOS_FIJOS_GENERICOS,
  "Sala de Billar": [
    { id: "escenario", tipo: "mesa-central", label: "Área principal", x: 30, y: 30, w: 40, h: 40 },
    { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
  ],
  "Terraza": [
    { id: "acceso", tipo: "acceso", label: "Acceso", x: 45, y: 90, w: 10, h: 6 },
    { id: "bar", tipo: "bar", label: "Bar", x: 4, y: 40, w: 8, h: 16 },
  ],
};

export const getElementosFijosPorSalon = (salon) =>
  ELEMENTOS_FIJOS_POR_SALON[salon] || ELEMENTOS_FIJOS_GENERICOS;

export const siguientePosicionMesa = (mesas) => {
  const cols = 5;
  const marginX = 15;
  const startY = 22;
  const endY = 82;
  const stepX = (100 - marginX * 2) / (cols - 1);
  const totalRowsAntes = Math.max(1, Math.ceil((mesas.length + 1) / cols));
  const stepY = totalRowsAntes > 1 ? (endY - startY) / Math.max(1, totalRowsAntes - 1) : 0;
  const idx = mesas.length;
  const row = Math.floor(idx / cols);
  const colIdx = idx % cols;
  const col = row % 2 === 0 ? colIdx : cols - 1 - colIdx;
  return {
    x: Math.round(marginX + stepX * col),
    y: Math.round(startY + stepY * row),
  };
};

export const nuevoIdMesa = () => {
  const rnd = Math.random().toString(36).slice(2, 8);
  return `MSA-${Date.now().toString(36)}-${rnd}`;
};

export const computarCapacidad = (mesas) => {
  if (!Array.isArray(mesas) || mesas.length === 0) return 0;
  return mesas.reduce((sum, m) => sum + (Number(m && m.capacidad) || 0), 0);
};

export const agruparMesasPorEstado = (mesas) => {
  const base = {
    mesas: { DISPONIBLE: 0, RESERVADA: 0, PROTOCOLO: 0, BLOQUEADA: 0 },
    capacidad: { total: 0, DISPONIBLE: 0, RESERVADA: 0, PROTOCOLO: 0, BLOQUEADA: 0 },
  };
  if (!Array.isArray(mesas)) return base;
  return mesas.reduce((acc, m) => {
    if (!m) return acc;
    const cap = Number(m.capacidad) || 0;
    const est = m.estado in acc.mesas ? m.estado : "DISPONIBLE";
    acc.mesas[est] += 1;
    acc.capacidad[est] += cap;
    acc.capacidad.total += cap;
    return acc;
  }, base);
};

export const salonesUtilizados = (evento) => {
  if (!evento) return [];
  const arr = [];
  if (Array.isArray(evento.salones)) {
    evento.salones.forEach((s) => {
      if (s && typeof s === "string" && !arr.includes(s)) arr.push(s);
    });
  }
  if (evento.salon && !arr.includes(evento.salon)) arr.push(evento.salon);
  return arr;
};

/* ---------- Utilidades storage (cache local) ---------- */

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJson = (key, fallback) => {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

/* ---------- Sincronización con el backend ---------- */

/**
 * `hidratarEventos()` obtiene el listado real desde el backend y actualiza
 * el cache local. Idempotente y silencioso ante errores de red (mantiene
 * cache actual). Debe llamarse en el mount de las pantallas que consumen
 * eventos.
 */
export const hidratarEventos = async () => {
  try {
    const { data } = await api.get("/eventos");
    if (Array.isArray(data)) {
      writeJson(KEY_EVENTOS, data);
      return data;
    }
  } catch {
    /* Fallback silencioso: se conserva el cache o los mocks seed. */
  }
  return readJson(KEY_EVENTOS, []) || [];
};

/**
 * Versión pública (sin token): usa `/api/eventos/publicos` para listar
 * únicamente lo visible al socio. También actualiza el cache local.
 */
export const hidratarEventosPublicos = async () => {
  try {
    const { data } = await api.get("/eventos/publicos");
    if (Array.isArray(data)) {
      // Mezcla suave: reemplaza sólo entradas públicas, preserva las de admin.
      const previos = Array.isArray(readJson(KEY_EVENTOS, null))
        ? readJson(KEY_EVENTOS, [])
        : [];
      const ids = new Set(data.map((ev) => ev.id));
      const soloAdmin = previos.filter((ev) => !ids.has(ev.id));
      const mezcla = [...data, ...soloAdmin];
      writeJson(KEY_EVENTOS, mezcla);
      return data;
    }
  } catch {
    /* ignore */
  }
  return [];
};

/* ---------- Compatibilidad con la Ola 0 (seed local) ---------- */

const fechaHumanaAISO = (texto) => {
  const MESES = {
    enero: "01", febrero: "02", marzo: "03", abril: "04", mayo: "05", junio: "06",
    julio: "07", agosto: "08", septiembre: "09", octubre: "10", noviembre: "11", diciembre: "12",
  };
  if (!texto || typeof texto !== "string") return "";
  const m = texto.toLowerCase().match(/^(\d{1,2})\s*de\s*([a-záéíóú]+),?\s*(\d{4})$/i);
  if (!m) return "";
  const dia = String(parseInt(m[1], 10)).padStart(2, "0");
  const mesKey = m[2].normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const mes = MESES[mesKey];
  if (!mes) return "";
  return `${m[3]}-${mes}-${dia}`;
};

const horaHumanaAHHMM = (texto) => {
  if (!texto || typeof texto !== "string") return "";
  const m = texto.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}:${m[2]}`;
};

/**
 * Antes esta función sembraba el cache local. Ahora la fuente de verdad
 * es el backend, pero durante la primera carga (antes de que el fetch
 * async resuelva) devolvemos los mocks para no mostrar UI vacía. El cache
 * se sustituye por los datos reales cuando `hidratarEventos()` resuelva.
 */
export const seedAdminEventos = () => {
  const stored = readJson(KEY_EVENTOS, null);
  if (Array.isArray(stored) && stored.length > 0) return stored;

  const nowIso = new Date().toISOString();
  const seed = eventosMock.map((ev) => ({
    id: ev.id,
    nombre: ev.nombre,
    subtitulo: "",
    descripcionCorta: ev.descripcion
      ? ev.descripcion.split(".").slice(0, 1).join(".") + "."
      : "",
    imagenPrincipal: ev.imagen || "",
    galeria: [],
    fecha: fechaHumanaAISO(ev.fecha) || "",
    fechaLegible: ev.fecha || "",
    hora: horaHumanaAHHMM(ev.hora) || "",
    horaLegible: ev.hora || "",
    salon: ev.salon || "",
    tipoEvento: "Otro",
    edadMinima: 18,
    tipoAcceso: TIPO_ACCESO.SOLO_SOCIOS,
    estado: ESTADO_EVENTO.BORRADOR,
    capacidad: (ev.mesas || []).reduce((s, m) => s + (m.capacidad || 0), 0),
    boletosVendidos: 0,
    createdAt: nowIso,
    updatedAt: nowIso,
  }));
  writeJson(KEY_EVENTOS, seed);
  return seed;
};

/* ---------- Reads (síncronos desde cache) ---------- */

const readAll = () => {
  const arr = readJson(KEY_EVENTOS, null);
  if (Array.isArray(arr)) {
    const limpio = arr.filter((ev) => ev && typeof ev.id === "string" && ev.id);
    if (limpio.length !== arr.length) writeJson(KEY_EVENTOS, limpio);
    return limpio;
  }
  return seedAdminEventos();
};

export const listarEventos = () => readAll().slice();

export const getEventoById = (id) => {
  if (!id) return null;
  return readAll().find((ev) => ev.id === id) || null;
};

/* ---------- Writes (async · backend + cache) ---------- */

const _upsertCache = (evento) => {
  if (!evento || !evento.id) return;
  const arr = readAll();
  const idx = arr.findIndex((e) => e.id === evento.id);
  if (idx >= 0) arr[idx] = evento;
  else arr.push(evento);
  writeJson(KEY_EVENTOS, arr);
};

/**
 * Crea o actualiza un evento en el backend. Si `datos.id` existe se hace
 * PUT; si no, POST. Devuelve la entidad persistida (nunca lanza).
 */
export const guardarEvento = async (datos) => {
  const payload = { ...(datos || {}) };
  // Los timestamps los maneja el backend (defaults + onupdate). Enviarlos
  // como strings ISO causa `StatementError` en SQLite. Los omitimos aquí
  // como refuerzo defensivo (el backend también los ignora).
  delete payload.createdAt;
  delete payload.updatedAt;
  const editar = !!payload.id;
  try {
    const { data } = editar
      ? await api.put(`/eventos/${payload.id}`, payload)
      : await api.post("/eventos", payload);
    _upsertCache(data);
    logBitacora({
      accion: editar ? ACCIONES.EDITAR_EVENTO : ACCIONES.CREAR_EVENTO,
      modulo: MODULOS.EVENTOS,
      detalle: `${editar ? "Evento editado" : "Evento creado"}: ${data.nombre} (${data.id}).`,
    });
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};

/* ---------- Filtros ---------- */

const finalizados = new Set([
  ESTADO_EVENTO.VENTA_FINALIZADA,
  ESTADO_EVENTO.EVENTO_REALIZADO,
  ESTADO_EVENTO.EVENTO_CERRADO,
]);

export const aplicarFiltro = (eventos, filtroId) => {
  if (!Array.isArray(eventos)) return [];
  switch (filtroId) {
    case "proximos":
      return eventos.filter(
        (ev) =>
          ev.estado === ESTADO_EVENTO.PUBLICADO ||
          ev.estado === ESTADO_EVENTO.BORRADOR,
      );
    case "venta_activa":
      return eventos.filter((ev) => ev.estado === ESTADO_EVENTO.VENTA_ACTIVA);
    case "venta_pausada":
      return eventos.filter((ev) => ev.estado === ESTADO_EVENTO.VENTA_PAUSADA);
    case "finalizados":
      return eventos.filter((ev) => finalizados.has(ev.estado));
    case "cancelados":
      return eventos.filter((ev) => ev.estado === ESTADO_EVENTO.CANCELADO);
    case "todos":
    default:
      return eventos.slice();
  }
};

export const buscar = (eventos, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return eventos;
  return eventos.filter((ev) => {
    const campos = [ev.nombre, ev.salon, ev.tipoEvento, ev.subtitulo]
      .filter(Boolean)
      .map((s) => s.toString().toLowerCase());
    return campos.some((s) => s.includes(q));
  });
};

export const ordenarPorFecha = (eventos, direccion = "asc") => {
  const arr = eventos.slice();
  arr.sort((a, b) => {
    const av = a.fecha || "";
    const bv = b.fecha || "";
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    return direccion === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
  });
  return arr;
};

export const formatearFechaCorta = (isoDate, fallback = "") => {
  if (!isoDate) return fallback;
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return fallback;
  const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
};

/* ---------- Checklist / publicación (Paso 5) ---------- */

const hoyMedianoche = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseISODate = (s) => {
  if (!s || typeof s !== "string") return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const checklistPublicacion = (evento) => {
  const ev = evento || {};
  const esAG = (ev.modalidad || "MESAS") === "ACCESO_GENERAL";
  const checks = [];
  const add = (grupo, id, label, ok, paso, motivo = "") =>
    checks.push({ grupo, id, label, ok: !!ok, paso, motivo });

  add("Información general", "nombre", "Nombre del evento", !!(ev.nombre && ev.nombre.trim()), 1);
  add("Información general", "descripcion", "Descripción", !!(ev.descripcionCorta && ev.descripcionCorta.trim()), 1);
  add("Información general", "imagen", "Imagen principal del evento", !!(ev.imagenPrincipal && ev.imagenPrincipal.trim()), 1, (!ev.imagenPrincipal || !ev.imagenPrincipal.trim()) ? "Sube o pega la URL de la imagen principal antes de publicar." : "");
  add("Información general", "fechaHora", "Fecha y hora del evento", !!(ev.fecha && ev.hora), 1);
  add("Información general", "salon", "Salón(es)", (Array.isArray(ev.salones) && ev.salones.length > 0) || !!ev.salon, 1);
  add("Información general", "tipoEvento", "Tipo de evento", !!ev.tipoEvento, 1);

  add("Configuración", "tipoAcceso", "Tipo de acceso", !!ev.tipoAcceso, 2);
  add("Configuración", "edadMinima", "Edad mínima", ev.edadMinima !== "" && ev.edadMinima !== null && ev.edadMinima !== undefined, 2);
  add("Configuración", "limiteBoletos", "Límite de boletos", Number(ev.limiteBoletosPorUsuario) >= 1, 2);
  add("Configuración", "ventaInicio", "Inicio de venta", !!ev.ventaInicioFecha, 2);
  add("Configuración", "ventaCierre", "Cierre de venta", !!ev.ventaCierreFecha, 2);

  let capTotal = 0;
  let capDisponible = 0;

  if (esAG) {
    // Acceso General · requisitos de salones (no exige plano/escenario/mesas).
    const salonesAG = Array.isArray(ev.salonesAcceso) ? ev.salonesAcceso : [];
    const activos = salonesAG.filter((s) => s.activo);
    capTotal = activos.reduce((s, x) => s + (Number(x.capacidad) || 0), 0);
    capDisponible = capTotal;
    const capOk = activos.length > 0 && activos.every((x) => Number(x.capacidad) > 0);
    const precioOk = activos.length > 0 && activos.every((x) => Number(x.precioPorBoleto) > 0);
    add("Salones y boletos", "salonActivo", "Al menos un salón activo", activos.length > 0, 3, activos.length === 0 ? "Agrega y activa al menos un salón vendible." : "");
    add("Salones y boletos", "salonCapacidad", "Todos los salones activos con capacidad válida", capOk, 3, !capOk ? "Cada salón activo debe tener capacidad mayor que cero." : "");
    add("Salones y boletos", "salonPrecio", "Todos los salones activos con precio mayor que cero", precioOk, 3, !precioOk ? "Cada salón activo debe tener un precio mayor que cero." : "");
  } else {
    const mesas = Array.isArray(ev.mesas) ? ev.mesas : [];
    capTotal = mesas.reduce((s, m) => s + (Number(m.capacidad) || 0), 0);
    const numeros = mesas.map((m) => m.numero);
    const hayDuplicados = new Set(numeros).size !== numeros.length;
    const sinCapacidad = mesas.some((m) => !Number.isFinite(Number(m.capacidad)) || Number(m.capacidad) <= 0);
    const disponibles = mesas.filter((m) => m.estado === ESTADO_MESA.DISPONIBLE);
    capDisponible = disponibles.reduce((s, m) => s + (Number(m.capacidad) || 0), 0);
    // Colisión: dos mesas cuyos centros están a menos de 9% (mismo umbral que
    // el editor de arrastre) se consideran empalmadas. Bloquea la publicación.
    const COLISION_MIN = 9;
    let hayColisiones = false;
    for (let i = 0; i < mesas.length && !hayColisiones; i++) {
      for (let j = i + 1; j < mesas.length; j++) {
        const dx = (Number(mesas[i].x) || 0) - (Number(mesas[j].x) || 0);
        const dy = (Number(mesas[i].y) || 0) - (Number(mesas[j].y) || 0);
        if (Math.hypot(dx, dy) < COLISION_MIN) {
          hayColisiones = true;
          break;
        }
      }
    }

    add("Plano y mesas", "mesasExisten", "Al menos una mesa", mesas.length > 0, 3);
    add("Plano y mesas", "capacidadTotal", "Capacidad total mayor que cero", capTotal > 0, 3);
    add("Plano y mesas", "capacidadDisponible", "Existe capacidad disponible para venta", capDisponible > 0, 3, capDisponible === 0 ? "No hay mesas en estado Disponible." : "");
    add("Plano y mesas", "sinDuplicados", "Numeración de mesas sin duplicados", !hayDuplicados, 3, hayDuplicados ? "Hay números de mesa duplicados." : "");
    add("Plano y mesas", "capacidadValida", "Todas las mesas tienen capacidad válida", !sinCapacidad, 3, sinCapacidad ? "Alguna mesa no tiene capacidad válida." : "");
    add("Plano y mesas", "sinColisiones", "Ninguna mesa se empalma con otra", !hayColisiones, 3, hayColisiones ? "Hay mesas superpuestas en el plano." : "");

    const salones = salonesUtilizados(ev);
    const precios = ev.precios && typeof ev.precios === "object" ? ev.precios : {};
    const preciosFaltantes = salones.filter((s) => !precios[s] || Number(precios[s]) <= 0);
    const consistencia = capTotal === mesas.reduce((s, m) => s + (Number(m.capacidad) || 0), 0);

    add("Configuración comercial", "precios", "Precio válido para cada salón utilizado", preciosFaltantes.length === 0, 4, preciosFaltantes.length > 0 ? `Falta precio para: ${preciosFaltantes.join(", ")}.` : "");
    add("Configuración comercial", "capacidadVendible", "Capacidad vendible mayor que cero", capDisponible > 0, 4);
    add("Configuración comercial", "totalesConsistentes", "Totales de capacidad consistentes", consistencia, 4);
  }

  const evtDate = parseISODate(ev.fecha);
  const ventaIni = parseISODate(ev.ventaInicioFecha);
  const ventaFin = parseISODate(ev.ventaCierreFecha);
  const hoy = hoyMedianoche();

  if (evtDate) add("Validaciones cruzadas", "fechaFutura", "La fecha del evento es posterior a hoy", evtDate >= hoy, 1, evtDate < hoy ? "La fecha del evento ya pasó." : "");
  if (ventaIni && ventaFin) add("Validaciones cruzadas", "cierreDespuesInicio", "Cierre de venta posterior al inicio", ventaFin >= ventaIni, 2, ventaFin < ventaIni ? "El cierre de venta es antes del inicio." : "");
  if (ventaIni && evtDate) add("Validaciones cruzadas", "inicioAntesDelEvento", "El inicio de venta es antes del evento", ventaIni <= evtDate, 2, ventaIni > evtDate ? "El inicio de venta es posterior al evento." : "");
  if (ventaFin && evtDate) add("Validaciones cruzadas", "cierreAntesDelEvento", "El cierre de venta es antes del evento", ventaFin <= evtDate, 2, ventaFin > evtDate ? "El cierre de venta es posterior al evento." : "");

  return {
    checks,
    listo: checks.every((c) => c.ok),
    resumen: { capacidadTotal: capTotal, capacidadDisponible: capDisponible, totalMesas: (Array.isArray(ev.mesas) ? ev.mesas.length : 0) },
  };
};

export const estadoDespuesDePublicar = (evento) => {
  const ini = parseISODate(evento && evento.ventaInicioFecha);
  if (!ini) return ESTADO_EVENTO.PUBLICADO;
  const hoy = hoyMedianoche();
  return ini > hoy ? ESTADO_EVENTO.PUBLICADO : ESTADO_EVENTO.VENTA_ACTIVA;
};

/* ---------- Writes (async · publicar / cancelar / cerrar / duplicar) ---------- */

export const publicarEvento = async ({ id, sesion, notificar }) => {
  if (!id) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/publicar`, { notificar: !!notificar });
    _upsertCache(data);
    logBitacora({
      usuario: sesion?.usuario,
      accion: ACCIONES.PUBLICAR,
      modulo: MODULOS.EVENTOS,
      detalle: `Evento publicado: ${data.nombre} → ${data.estado}.`,
    });
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};

export const cancelarEvento = async ({ id, motivo, sesion }) => {
  if (!id || !motivo || !motivo.trim()) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/cancelar`, { motivo: motivo.trim() });
    _upsertCache(data);
    logBitacora({
      usuario: sesion?.usuario,
      accion: ACCIONES.CANCELAR,
      modulo: MODULOS.EVENTOS,
      detalle: `Evento cancelado: ${data.nombre}. Motivo: ${data.motivoCancelacion}.`,
    });
    return data;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 409) return { error: "cerrado" };
    return { error: detail || "network" };
  }
};

export const cerrarEvento = async ({ id, motivo, sesion }) => {
  if (!id || !motivo || !motivo.trim()) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/cerrar`, { motivo: motivo.trim() });
    _upsertCache(data);
    logBitacora({
      usuario: sesion?.usuario,
      accion: ACCIONES.CERRAR,
      modulo: MODULOS.EVENTOS,
      detalle: `Evento cerrado: ${data.nombre}. Motivo: ${data.motivoCierre}.`,
    });
    return data;
  } catch (err) {
    if (err?.response?.status === 409) return { error: "fecha-futura" };
    return { error: err?.response?.data?.detail || "network" };
  }
};

export const pausarEvento = async ({ id, sesion }) => {
  if (!id) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/pausar-venta`);
    _upsertCache(data);
    logBitacora({
      usuario: sesion?.usuario,
      accion: ACCIONES.PAUSAR,
      modulo: MODULOS.EVENTOS,
      detalle: `Venta pausada: ${data.nombre}.`,
    });
    return data;
  } catch (err) {
    return {
      error: err?.response?.data?.detail || "network",
      status: err?.response?.status,
    };
  }
};

export const reanudarEvento = async ({ id, sesion }) => {
  if (!id) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/reanudar-venta`);
    _upsertCache(data);
    logBitacora({
      usuario: sesion?.usuario,
      accion: ACCIONES.REANUDAR,
      modulo: MODULOS.EVENTOS,
      detalle: `Venta reanudada: ${data.nombre}.`,
    });
    return data;
  } catch (err) {
    return {
      error: err?.response?.data?.detail || "network",
      status: err?.response?.status,
    };
  }
};

export const duplicarEvento = async ({ id }) => {
  if (!id) return null;
  try {
    const { data } = await api.post(`/eventos/${id}/duplicar`);
    _upsertCache(data);
    logBitacora({
      accion: ACCIONES.CREAR_EVENTO,
      modulo: MODULOS.EVENTOS,
      detalle: `Evento duplicado: ${data.nombre} (${data.id}).`,
    });
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};

/* ---------- Visibilidad para el socio ---------- */

const ESTADOS_VISIBLES_SOCIO = new Set([
  ESTADO_EVENTO.PUBLICADO,
  ESTADO_EVENTO.VENTA_ACTIVA,
  ESTADO_EVENTO.VENTA_PAUSADA,
  ESTADO_EVENTO.VENTA_FINALIZADA,
  ESTADO_EVENTO.EVENTO_REALIZADO,
]);

const capitalizarMesFecha = (isoDate) => {
  const d = parseISODate(isoDate);
  if (!d) return "";
  const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
};

const fmtDineroSocio = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "$ —";
  return `$${num.toLocaleString("es-MX")} MXN`;
};

const estadoSocio = (ev) => {
  if (ev.estado === ESTADO_EVENTO.PUBLICADO) return "Próximamente";
  if (ev.estado === ESTADO_EVENTO.VENTA_PAUSADA) return "Venta pausada";
  if (ev.estado === ESTADO_EVENTO.VENTA_FINALIZADA) return "Venta finalizada";
  if (ev.estado === ESTADO_EVENTO.EVENTO_REALIZADO) return "Evento realizado";
  if (ev.estado === ESTADO_EVENTO.VENTA_ACTIVA) {
    const desg = agruparMesasPorEstado(ev.mesas || []);
    const disp = desg.capacidad.DISPONIBLE;
    const total = desg.capacidad.total;
    if (total > 0 && disp / total < 0.2) return "Últimos lugares";
    return "Venta disponible";
  }
  return "Próximamente";
};

export const adaptarEventoParaSocio = (ev) => {
  if (!ev) return null;
  const salones = salonesUtilizados(ev);
  const salon = salones[0] || ev.salon || "";
  const preciosMap =
    ev.precios && typeof ev.precios === "object" ? ev.precios : {};
  const preciosPorSalon = salones
    .map((s) => ({ salon: s, precioNum: Number(preciosMap[s]) || 0 }))
    .filter((p) => p.precioNum > 0)
    .map((p) => ({
      salon: p.salon,
      precio: fmtDineroSocio(p.precioNum),
      precioNum: p.precioNum,
    }));
  const preciosDistintos = Array.from(
    new Set(preciosPorSalon.map((p) => p.precioNum)),
  );
  const precioMultiple = preciosDistintos.length > 1;
  const precioNum =
    (preciosPorSalon[0] && preciosPorSalon[0].precioNum) ||
    (ev.precios ? Object.values(ev.precios).find((v) => Number(v) > 0) : null) ||
    0;
  const mesas = Array.isArray(ev.mesas) ? ev.mesas : [];
  const mesasSocio = mesas.map((m) => ({
    numero: m.numero,
    capacidad: Number(m.capacidad) || 0,
    disponibles: m.estado === ESTADO_MESA.DISPONIBLE ? Number(m.capacidad) || 0 : 0,
    x: m.x,
    y: m.y,
  }));
  return {
    id: ev.id,
    nombre: ev.nombre,
    subtitulo: ev.subtitulo || "",
    descripcion: ev.descripcionCorta || "",
    imagen: ev.imagenPrincipal || "",
    fecha: capitalizarMesFecha(ev.fecha) || ev.fechaLegible || "",
    hora: ev.hora || ev.horaLegible || "",
    salon,
    lugar: salon,
    estado: estadoSocio(ev),
    estadoInterno: ev.estado,
    // Sprint 57 · F4C · la modalidad real determina el flujo de compra del socio.
    modalidad: ev.modalidad || "MESAS",
    // Resumen de precios AG (solo lectura) construido por el backend.
    accesoResumen: ev.accesoResumen || null,
    precio: fmtDineroSocio(precioNum),
    preciosPorSalon,
    precioMultiple,
    codigoVestimenta: ev.codigoVestimenta || "",
    informacionAdicional: ev.informacionAdicional || "",
    mesas: mesasSocio,
  };
};

export const getEventosVisiblesParaSocio = () =>
  readAll()
    .filter((ev) => ESTADOS_VISIBLES_SOCIO.has(ev.estado))
    .map(adaptarEventoParaSocio);

export const getEventoVisibleByIdParaSocio = (id) => {
  if (!id) return null;
  const ev = readAll().find((e) => e.id === id);
  if (!ev) return null;
  if (!ESTADOS_VISIBLES_SOCIO.has(ev.estado)) return null;
  return adaptarEventoParaSocio(ev);
};
